/**
 * ReferenceCard Component
 *
 * Compact card (200px fixed width) for person selection with step badge,
 * preview, and select dropdown. Used in the Avatar Generate Alt Layout.
 */

import { User } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { IReferenceCardProps } from '@/@types/avatars';

export const ReferenceCard = ({
  selectedPersonId,
  onPersonChange,
  referencePersons,
  isLoading = false,
}: IReferenceCardProps) => {
  const selectedPerson = referencePersons.find(p => p.id === selectedPersonId);

  return (
    <div className="w-[200px] shrink-0 rounded-2xl border border-border bg-card p-5">
      <SectionHeader step={1} label="Reference" />

      <div className="mt-4 flex flex-col items-center gap-4">
        {/* Person Preview */}
        <div className="flex items-center justify-center w-20 h-20 rounded-full border border-border overflow-hidden bg-background-input">
          {selectedPerson?.imageUrl ? (
            <img
              src={selectedPerson.imageUrl}
              alt={selectedPerson.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-8 h-8 text-muted-foreground" />
          )}
        </div>

        {/* Person Select */}
        <Select
          value={selectedPersonId ?? ""}
          onValueChange={onPersonChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full" aria-label="Select reference person">
            <SelectValue placeholder="Select person..." />
          </SelectTrigger>
          <SelectContent>
            {referencePersons.map(person => (
              <SelectItem key={person.id} value={person.id} textValue={person.name}>
                <div className="flex items-center gap-2">
                  {person.imageUrl && (
                    <img
                      src={person.imageUrl}
                      alt={person.name}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  )}
                  <span>{person.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
